import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
    getKpiGetPointProgressThunk,
    getPlanGetByPointIdThunk,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";

import { useCatalog } from "@/domains/product";
import {
    useAppDispatch,
    useAppSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { Header } from "@/shared/layout";
import { BlockList } from "@/shared/pageComposition/BlockList";
import { Meta } from "@/shared/seo";

import { StyledAPContainer } from "../../Styled";
import { getInfoTabPlanBlocks } from "./InfoTabPlan.blocks";
import {
    FinGroupFact,
    InfoTabPlanModel,
    PlanDataTuple,
} from "./InfoTabPlan.model";
import { getInfoTabPlanCapabilities } from "./InfoTabPlan.policy";

const InfoTabPlan = () => {
    const role = useProtectedUserRoleSelector();
    const { pointId = "" } = useParams<{ pointId: string }>();

    const { plans, offlineMode, facts } = useAppSelector(
        (state) => state.offline,
    );
    const { products } = useCatalog();

    const dispatch = useAppDispatch();

    const currentPointPlan = useMemo(
        () => Object.entries(plans[pointId] || {}) as PlanDataTuple[],
        [plans, pointId],
    );
    const currentRoutePlan = useMemo(
        () => Object.entries(plans["unassignedPlan"] || {}) as PlanDataTuple[],
        [plans],
    );
    const currentPointFact = useMemo(
        () => facts[pointId] || {},
        [facts, pointId],
    );

    const hasPointPlan = currentPointPlan.length > 0;
    const hasRoutePlan = currentRoutePlan.length > 0;

    const [activeTab, setActiveTab] = useState<string>(
        hasPointPlan ? "tt" : "route",
    );

    useEffect(() => {
        if (!hasPointPlan && hasRoutePlan && activeTab !== "route") {
            setActiveTab("route");
        } else if (hasPointPlan && !hasRoutePlan && activeTab !== "tt") {
            setActiveTab("tt");
        }
    }, [hasPointPlan, hasRoutePlan, activeTab]);

    const currentPointFinGroupsFact = useMemo(() => {
        const result: Record<string, FinGroupFact> = {};

        Object.entries(currentPointFact).forEach(([barcode, fact]) => {
            const finGroup = products[barcode]?.params?.fin_group_detail
                ?.value as string;
            if (!finGroup) return;

            result[finGroup] ??= { b: 0, l: 0, m: 0 };

            if (fact?.bottle > 0) result[finGroup].b += fact.bottle;
            if (fact?.capacity > 0) result[finGroup].l += fact.capacity;
            if (fact?.money > 0) result[finGroup].m += fact.money;
        });

        return result;
    }, [currentPointFact, products]);

    const availableTabs = useMemo(() => {
        const tabs = [];
        if (hasPointPlan) {
            tabs.push({ label: "На ТТ", value: "tt" });
        }
        if (hasRoutePlan) {
            tabs.push({ label: "Додатковий", value: "route" });
        }
        return tabs;
    }, [hasPointPlan, hasRoutePlan]);

    useEffect(() => {
        if (!offlineMode) {
            dispatch(
                getPlanGetByPointIdThunk({
                    pointId,
                    targetDate: dayjs().format(backendDateFormat.date),
                }),
            );
            dispatch(
                getKpiGetPointProgressThunk({
                    pointId,
                    targetDate: dayjs().format(backendDateFormat.date),
                }),
            );
        }
    }, [dispatch, pointId, offlineMode]);

    const capabilities = useMemo(
        () => getInfoTabPlanCapabilities(role),
        [role],
    );

    const model: InfoTabPlanModel = useMemo(
        () => ({
            capabilities,
            hasPointPlan,
            hasRoutePlan,
            activeTab,
            setActiveTab,
            availableTabs,
            currentPointPlan,
            currentRoutePlan,
            currentPointFinGroupsFact,
        }),
        [
            capabilities,
            hasPointPlan,
            hasRoutePlan,
            activeTab,
            availableTabs,
            currentPointPlan,
            currentRoutePlan,
            currentPointFinGroupsFact,
        ],
    );

    const blocks = useMemo(() => getInfoTabPlanBlocks(model), [model]);

    return (
        <>
            <Meta title="TT Info MML" />
            <Header title="План продажів" back titleFontSize={16} />

            <StyledAPContainer>
                <BlockList blocks={blocks} model={model} />
            </StyledAPContainer>
        </>
    );
};

export default InfoTabPlan;
